// Pulpity własne konta (`/api/pulpity`, docs/pwa/PROJEKT.md 9): stan poza Reactem, wczytywany przy wejściu na ekran
// i po zdarzeniu SSE `konto` z innego telefonu (`pulpity.zmiana`). Własny zapis podnosi wersję lokalnie - zdarzenie
// o tej samej wersji nic nie pobiera. Ostatni zapis wygrywa, bez historii (U-8).
import { useSyncExternalStore } from "react";
import type {
  AccountEvent, Card, Dashboard, DashboardReply, DashboardsChanged, DashboardsReply, DeviceLayout, DeviceLayoutReply, OKReply, RodzajEkranu,
  VersionReply,
} from "../gen/kontrakt";
import { zapytanie } from "./zaplecze";

export type Uklady = Dashboard["uklady"];

export interface StanPulpitow {
  stan: "laduje" | "jest" | "blad";
  pulpity: readonly Dashboard[];
  /** Pulpity klientów serwisanta (aktywne zgody, docs/pwa/SERWIS.md) - tylko do odczytu (U-4). */
  klienci: DashboardsReply["klienci"];
}

/** Pulpity klientów jako jedna lista: klient i jego pulpit. */
export function pulpityKlientow(p: StanPulpitow): { klient: string; pulpit: Dashboard }[] {
  return p.klienci.flatMap((k) => k.pulpity.map((d) => ({ klient: k.nazwa, pulpit: d })));
}

let stan: StanPulpitow = { stan: "laduje", pulpity: [], klienci: [] };
const sluchacze = new Set<() => void>();
let wczytywanie: Promise<void> | null = null;

function ustaw(s: StanPulpitow): void {
  stan = s;
  for (const f of sluchacze) f();
}

function zmienPulpit(id: number, zmiana: (d: Dashboard) => Dashboard): void {
  ustaw({ ...stan, pulpity: stan.pulpity.map((d) => (d.id === id ? zmiana(d) : d)) });
}

/** Wczytuje pulpity z API (jedno zapytanie naraz). */
export function wczytajPulpity(): Promise<void> {
  wczytywanie ??= zapytanie<DashboardsReply>("GET", "/api/pulpity")
    .then((o) => ustaw({ stan: "jest", pulpity: o.pulpity, klienci: o.klienci }))
    .catch(() => ustaw({ ...stan, stan: stan.stan === "jest" ? "jest" : "blad" }))
    .finally(() => { wczytywanie = null; });
  return wczytywanie;
}

/** Zdarzenie SSE `konto`: zmiana z innego telefonu - pobranie, gdy lokalnie jest starsza wersja (albo lista);
 *  `sn` - układ pulpitu sztuki. */
export function przyjmijZdarzenieKonta(e: AccountEvent): void {
  if (e.rodzaj !== "pulpity.zmiana") return;
  const z = e.dane as DashboardsChanged;
  if (z.sn) {
    const u = ukladySztuk.get(z.sn);
    if (u && u.wersja !== z.wersja) void wczytajUkladSztuki(z.sn);
    return;
  }
  if (stan.stan === "laduje") return;
  const d = stan.pulpity.find((x) => x.id === z.id);
  if (z.id !== 0 && z.wersja !== 0 && d && d.wersja >= z.wersja) return;
  void wczytajPulpity();
}

export async function nowyPulpit(nazwa: string): Promise<Dashboard> {
  const o = await zapytanie<DashboardReply>("POST", "/api/pulpity", { nazwa });
  ustaw({ ...stan, pulpity: [...stan.pulpity, o.pulpit] });
  return o.pulpit;
}

export async function zmienNazwe(id: number, nazwa: string): Promise<void> {
  const o = await zapytanie<VersionReply>("PATCH", `/api/pulpity/${id}`, { nazwa });
  zmienPulpit(id, (d) => ({ ...d, nazwa, wersja: o.wersja }));
}

export async function zapiszUklad(id: number, ekran: RodzajEkranu, karty: Card[]): Promise<void> {
  const d = stan.pulpity.find((x) => x.id === id);
  await zapiszUklady(id, { ...d?.uklady, [ekran]: karty });
}

/** Wszystkie warianty pulpitu naraz (kreator „dodaj na pulpit” dokłada kartę do każdego). */
export async function zapiszUklady(id: number, uklady: Uklady): Promise<void> {
  const o = await zapytanie<VersionReply>("PUT", `/api/pulpity/${id}/uklad`, { uklady });
  zmienPulpit(id, (x) => ({ ...x, uklady, wersja: o.wersja }));
}

export async function usunPulpit(id: number): Promise<void> {
  await zapytanie<VersionReply>("DELETE", `/api/pulpity/${id}`);
  ustaw({ ...stan, pulpity: stan.pulpity.filter((d) => d.id !== id) });
}

export async function przestawPulpity(ids: number[]): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/pulpity/kolejnosc", { ids });
  const po = new Map(ids.map((id, i) => [id, i]));
  ustaw({ ...stan, pulpity: [...stan.pulpity].sort((a, b) => (po.get(a.id) ?? 0) - (po.get(b.id) ?? 0)) });
}

// Układy pulpitów sztuk konta („Ułóż ekran”, `/api/urzadzenia/{sn}/uklad`): wersja 0 = układ domyślny linii.
const ukladySztuk = new Map<string, DeviceLayout>();

export async function wczytajUkladSztuki(sn: string): Promise<void> {
  const o = await zapytanie<DeviceLayoutReply>("GET", ukladAdres(sn)).catch(() => null);
  if (o) ustawUkladSztuki(sn, o.uklad);
}

/** `ukryte` - rodzaje kart i „rodzaj:element” (element karty, na wszystkich rodzajach ekranu). */
export async function zapiszUkladSztuki(sn: string, uklady: Uklady, ukryte: string[]): Promise<void> {
  const o = await zapytanie<VersionReply>("PUT", ukladAdres(sn), { uklady, ukryte });
  ustawUkladSztuki(sn, { uklady, ukryte, wersja: o.wersja });
}

/** „Przywróć domyślny”. */
export async function przywrocUkladSztuki(sn: string): Promise<void> {
  await zapytanie<VersionReply>("DELETE", ukladAdres(sn));
  ustawUkladSztuki(sn, { uklady: {}, ukryte: [], wersja: 0 });
}

const ukladAdres = (sn: string) => `/api/urzadzenia/${encodeURIComponent(sn)}/uklad`;

function ustawUkladSztuki(sn: string, u: DeviceLayout): void {
  ukladySztuk.set(sn, u);
  ustaw({ ...stan });
}

/** Układ pulpitu sztuki; null - jeszcze nie wczytany. */
export function useUkladSztuki(sn: string): DeviceLayout | null {
  useSyncExternalStore(subskrybuj, () => stan);
  return ukladySztuk.get(sn) ?? null;
}

/** Wylogowanie: bez pulpitów poprzedniego konta. */
export function wyczyscPulpity(): void {
  ukladySztuk.clear();
  ustaw({ stan: "laduje", pulpity: [], klienci: [] });
}

function subskrybuj(f: () => void): () => void {
  sluchacze.add(f);
  return () => sluchacze.delete(f);
}

export function usePulpity(): StanPulpitow {
  return useSyncExternalStore(subskrybuj, () => stan);
}
